import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Collections from "@/pages/Collections";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Stockists from "@/pages/Stockists";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import ProductDetail from "@/pages/ProductDetail";
import CategoryPage from "@/pages/CategoryPage";
import SpecLibrary from "@/pages/SpecLibrary";
import { CATEGORY_LINKS } from "@/data/products";
import { SiteContentProvider } from "@/content";

const SmoothScroll = ({ children }) => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);
  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ImageProtection = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname === "/specs") return undefined;
    const onContextMenu = (e) => {
      if (e.target.closest("img, video")) {
        e.preventDefault();
        toast.message("SanCub imagery is protected", {
          id: "image-protection",
          description: "Please contact sales@sancub.com for image requests.",
          duration: 2500,
        });
      }
    };
    const onDragStart = (e) => {
      if (e.target.closest("img, video")) e.preventDefault();
    };
    const onCopy = (e) => {
      if (e.target.closest && e.target.closest("img, video")) e.preventDefault();
    };
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("copy", onCopy);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("copy", onCopy);
    };
  }, [pathname]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/collections" element={<Collections />} />
        {CATEGORY_LINKS.map((c) => (
          <Route key={c.to} path={c.to} element={<CategoryPage category={c.label} />} />
        ))}
        <Route path="/specs" element={<SpecLibrary />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/stockists" element={<Stockists />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SmoothScroll>
          <ScrollToTop />
          <ImageProtection />
          <SiteContentProvider>
            <AppRouter />
          </SiteContentProvider>
          <Toaster position="top-center" richColors />
        </SmoothScroll>
      </BrowserRouter>
    </div>
  );
}

export default App;
